import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import {
  UserReportGenerateNotesModalWrapper,
  UserReportGenerateNotesTitle,
  UserReportGenerateNotesModalTextField,
  UserReportGenerateNotesModalNPIMessage,
  UserReportGenerateNotesButtonsRight,
  UserReportGenerateButtonLoader,
  UserReportGenerateNotesButtonsWrapper,
} from "./UserReport.styles";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { generateTreatmentNotes } from "./Services/UserReportService";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { getScreenNotes } from "./helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

const getEditButtonText = (mobile: boolean, notesEditable: boolean) => {
  if (mobile) {
    return notesEditable ? "REVERT" : "CUSTOMIZE";
  }
  return notesEditable ? "REVERT TO DEFAULT" : "CUSTOMIZE NOTES";
};

export const GenerateNotesModal: React.FC<{
  generateNotesModalOpen: DetailedRequisitionSerializer;
  setGenerateNotesModalOpen: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
  setFullReport: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
  setRecentDownland: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  generateNotesModalOpen,
  setGenerateNotesModalOpen,
  setFullReport,
  setRecentDownland,
}) => {
  const { physicalDeviceType } = useDeviceHook();
  const [notes, setNotes] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [notesEditable, setNotesEditable] = useState(false);
  const notesFieldReference = useRef<HTMLInputElement | null>(null);

  const originalNotes = getScreenNotes(
    generateNotesModalOpen.test_ordered!,
    generateNotesModalOpen,
  );

  useEffect(() => {
    if (generateNotesModalOpen && !notes) {
      setNotes(originalNotes);
    }
  }, [generateNotesModalOpen]);

  const { auth, setLoggedOut } = useAuthenticationHook();

  const generateNotes = async () => {
    if (notes && generateNotesModalOpen?.sfid && auth?.token) {
      setLoading(true);
      const newReport = await generateTreatmentNotes(
        `${notes} \n \n \nElectronically signed by ordering provider: ${generateNotesModalOpen?.physician_name} (NPI: ${generateNotesModalOpen?.npi})`,
        generateNotesModalOpen?.sfid,
        auth.token,
        setLoggedOut,
      );
      setLoading(false);
      if (newReport) {
        setFullReport(newReport);
        setRecentDownland(true);
        const tempAnchor = document.createElement("a");
        tempAnchor.download = "file.pdf";
        if (newReport.generated_notes_download_url) {
          tempAnchor.href = newReport.generated_notes_download_url;
          tempAnchor.click();
        }
        tempAnchor.remove();
        setGenerateNotesModalOpen(undefined);
      }
    }
  };

  useEffect(() => {
    if (notesEditable && notesFieldReference.current && notes) {
      notesFieldReference.current.focus();
      notesFieldReference.current.selectionStart = notes.length;
      notesFieldReference.current.selectionEnd = notes.length;
    }
  }, [notesEditable]);

  return (
    <Modal
      open={Boolean(generateNotesModalOpen)}
      onClose={() => setGenerateNotesModalOpen(undefined)}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <UserReportGenerateNotesModalWrapper deviceType={physicalDeviceType}>
        <UserReportGenerateNotesTitle deviceType={physicalDeviceType}>
          <div>
            {`GENERATE CLINIC VISIT NOTES: ${generateNotesModalOpen?.first_name} ${generateNotesModalOpen?.last_name}`}
          </div>
          <div>
            <Close onClick={() => setGenerateNotesModalOpen(undefined)} />
          </div>
        </UserReportGenerateNotesTitle>

        {notes && (
          <UserReportGenerateNotesModalTextField
            inputRef={notesFieldReference}
            value={notes}
            onChange={(e) => setNotes(e.target.value || originalNotes)}
            label=""
            multiline
            disabled={!notesEditable}
          />
        )}
        <UserReportGenerateNotesModalNPIMessage>
          {`Signed and submitted by ordering provider: ${generateNotesModalOpen?.physician_name} (NPI: ${generateNotesModalOpen?.npi})`}
        </UserReportGenerateNotesModalNPIMessage>
        <UserReportGenerateNotesButtonsWrapper>
          <Button
            label={getEditButtonText(
              physicalDeviceType === "mobile",
              notesEditable,
            )}
            onClick={() => {
              if (notesEditable) {
                setNotes(originalNotes);
              }
              setNotesEditable(!notesEditable);
            }}
            size="small"
            backgroundColor="white"
            textColor="greyDark"
            borderColor="greyDark"
          />
          <UserReportGenerateNotesButtonsRight>
            <Button
              label="CANCEL"
              onClick={() => setGenerateNotesModalOpen(undefined)}
              size="small"
              backgroundColor="white"
              textColor="greyDark"
              borderColor="greyDark"
            />
            {loading ? (
              <UserReportGenerateButtonLoader>
                <SpinnerLoading />
              </UserReportGenerateButtonLoader>
            ) : (
              <Button
                label="GENERATE"
                onClick={() => generateNotes()}
                size="small"
                backgroundColor="redMedium"
                textColor="white"
                className="generate-notes-button"
              />
            )}
          </UserReportGenerateNotesButtonsRight>
        </UserReportGenerateNotesButtonsWrapper>
      </UserReportGenerateNotesModalWrapper>
    </Modal>
  );
};
